import html2canvas from 'html2canvas';
import { analyticsAgent } from '../AnalyticTracker';
// Import XLSX conditionally to handle case where it's not installed
let XLSX: any;
try {
  XLSX = require('xlsx');
} catch (e) {
  console.warn(
    'XLSX library not available. Excel export functionality will be limited.',
  );
}

type ExportFormat = 'png' | 'csv' | 'xls' | 'xlsx';

interface ExportOptions {
  fileName?: string;
  title?: string;
  element?: HTMLElement;
  elementRef?: React.RefObject<HTMLElement>;
  data?: any[][];
  headers?: string[];
  scale?: number;
  trackingLabel?: string;
  onComplete?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Export element as PNG
 */
export const exportAsPNG = async (options: ExportOptions) => {
  const {
    fileName = 'export',
    title = 'Export',
    element,
    elementRef,
    scale = 2,
    trackingLabel,
    onComplete,
    onError,
  } = options;

  try {
    // Get element either from direct reference or React ref
    const targetElement = element || (elementRef?.current as HTMLElement);

    if (!targetElement) {
      throw new Error('No element found to export');
    }

    const canvas = await html2canvas(targetElement, {
      backgroundColor: null,
      scale,
      logging: false,
      allowTaint: true,
      useCORS: true,
    });

    // Convert to blob
    canvas.toBlob(blob => {
      if (!blob) {
        throw new Error('Failed to create blob');
      }

      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${fileName}.png`;
      document.body.appendChild(link);

      // Trigger download
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      // Track analytics
      if (trackingLabel) {
        analyticsAgent?.track(`Export PNG: ${trackingLabel}`, {
          title,
          format: 'png',
        });
      }

      if (onComplete) {
        onComplete();
      }
    }, 'image/png');
  } catch (error) {
    console.error('Error exporting as PNG:', error);
    if (onError) {
      onError(error as Error);
    }
  }
};

/**
 * Export data as CSV
 */
export const exportAsCSV = (options: ExportOptions) => {
  const {
    fileName = 'export',
    title = 'Export',
    data = [],
    headers = [],
    trackingLabel,
    onComplete,
    onError,
  } = options;

  try {
    // If headers provided and data is array of arrays, add headers as first row
    const contentArray = [...data];
    if (headers.length > 0 && Array.isArray(data[0])) {
      contentArray.unshift(headers);
    }

    // Convert array to CSV
    let csvContent = 'data:text/csv;charset=utf-8,';
    contentArray.forEach(row => {
      // Ensure each cell is properly escaped for CSV
      const rowStr = row
        .map(cell => {
          // Convert to string and escape quotes by doubling them
          const cellStr = String(cell).replace(/"/g, '""');
          // Wrap in quotes to handle commas, newlines, etc.
          return `"${cellStr}"`;
        })
        .join(',');
      csvContent += rowStr + '\r\n';
    });

    // Create download link
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `${fileName}.csv`);
    document.body.appendChild(link);

    // Trigger download
    link.click();
    document.body.removeChild(link);

    // Track analytics
    if (trackingLabel) {
      analyticsAgent?.track(`Export CSV: ${trackingLabel}`, {
        title,
        format: 'csv',
      });
    }

    if (onComplete) {
      onComplete();
    }
  } catch (error) {
    console.error('Error exporting as CSV:', error);
    if (onError) {
      onError(error as Error);
    }
  }
};

/**
 * Export data as Excel (XLS/XLSX)
 */
export const exportAsExcel = (options: ExportOptions) => {
  const {
    fileName = 'export',
    title = 'Export',
    data = [],
    headers = [],
    trackingLabel,
    onComplete,
    onError,
  } = options;

  try {
    // If XLSX is not available, show warning and fall back to CSV
    if (!XLSX) {
      console.warn('XLSX library not available, falling back to CSV export');
      exportAsCSV(options);
      return;
    }

    // Prepare data with headers if provided
    const contentArray = [...data];
    if (headers.length > 0 && Array.isArray(data[0])) {
      contentArray.unshift(headers);
    }

    // Create worksheet
    const worksheet = XLSX.utils.aoa_to_sheet(contentArray);

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

    // Generate file
    XLSX.writeFile(workbook, `${fileName}.xlsx`);

    // Track analytics
    if (trackingLabel) {
      analyticsAgent?.track(`Export Excel: ${trackingLabel}`, {
        title,
        format: 'xlsx',
      });
    }

    if (onComplete) {
      onComplete();
    }
  } catch (error) {
    console.error('Error exporting as Excel:', error);
    if (onError) {
      onError(error as Error);
    }

    // If XLSX fails for any reason, try falling back to CSV
    try {
      console.warn('Excel export failed, falling back to CSV export');
      exportAsCSV(options);
    } catch (csvError) {
      console.error('CSV fallback also failed:', csvError);
    }
  }
};

/**
 * Generic export function that can handle multiple formats
 */
export const exportData = (format: ExportFormat, options: ExportOptions) => {
  switch (format) {
    case 'png':
      return exportAsPNG(options);
    case 'csv':
      return exportAsCSV(options);
    case 'xls':
    case 'xlsx':
      return exportAsExcel(options);
    default:
      console.error(`Unsupported export format: ${format}`);
  }
};
